import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deactivateAccount, getMyAccount } from '../api/users';
import { getLinkedProviders, socialUnlink } from '../api/social';
import { getMarketingConsent, updateMarketingConsent } from '../api/marketing-consent';
import { getSession, hasSessionRole } from '../api/session';
import { SOCIAL_LOGIN_ENABLED, WEB_OAUTH_PROVIDERS, startOAuth } from '../config/oauth';
import type { OAuthProvider } from '../config/oauth';
import { ChangePasswordModal } from '../components/account/ChangePasswordModal';
import { EmailCredentialModal } from '../components/account/EmailCredentialModal';
import { PhoneVerifyModal } from '../components/account/PhoneVerifyModal';
import { HomeFooter } from '../components/home/HomeFooter';
import { HomeHeader } from '../components/home/HomeHeader';
import { ChevronIcon } from '../components/shared/Icons';
import { ReactComponent as SnsAppleBadge } from '../assets/icons/mobile/mobile-sns-apple.svg';
import { ReactComponent as SnsGoogleBadge } from '../assets/icons/mobile/mobile-sns-google.svg';
import { ReactComponent as SnsKakaoBadge } from '../assets/icons/mobile/mobile-sns-kakao.svg';
import { HEADER_SEARCH_KEYWORD_SUGGESTIONS } from '../config/searchSuggestions';
import { clearAuthSession, loadAuthSession } from '../data/authSession';
import { clearBlockedUserIds } from '../data/blockedUsers';
import {
  resolveAccountSettingsEmail,
  type AccountLinkProvider,
} from '../data/accountSettings';
import '../styles/account-settings.css';
import '../styles/phone-verify-modal.css';

function LinkDoneCheck() {
  return (
    <svg
      aria-hidden
      className="account-settings__check-glyph"
      fill="none"
      height="14"
      viewBox="0 0 14 14"
      width="14"
    >
      <path
        d="M3 7.15L5.65 9.8L11 4.45"
        stroke="#6d7a87"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function KakaoGlyph() {
  return <SnsKakaoBadge aria-hidden className="account-settings__sns-svg" />;
}

function GoogleGlyph() {
  return <SnsGoogleBadge aria-hidden className="account-settings__sns-svg" />;
}

function AppleGlyph() {
  return <SnsAppleBadge aria-hidden className="account-settings__sns-svg" />;
}

function EmailGlyph() {
  return (
    <svg aria-hidden fill="none" height="20" viewBox="0 0 20 20" width="20">
      <rect height="11" rx="1.5" stroke="#6d7a87" strokeWidth="1.5" width="15" x="2.5" y="4.5" />
      <path
        d="M3 5.5l7 5 7-5"
        stroke="#6d7a87"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

const SNS_LINK_ROWS = [
  { key: 'kakao', name: '카카오', Glyph: KakaoGlyph },
  { key: 'google', name: '구글', Glyph: GoogleGlyph },
  { key: 'apple', name: '애플', Glyph: AppleGlyph },
] as const;

export function AccountSettingsPage() {
  const navigate = useNavigate();
  const isAuthenticated = Boolean(loadAuthSession());
  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const headerSearchRef = useRef<HTMLDivElement | null>(null);

  const [email, setEmail] = useState(() => resolveAccountSettingsEmail());
  const [marketingOptIn, setMarketingOptIn] = useState<boolean | null>(null);
  const [marketingError, setMarketingError] = useState<string | null>(null);
  const [marketingPending, setMarketingPending] = useState(false);
  const marketingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [linked, setLinked] = useState<Record<AccountLinkProvider, boolean>>({
    kakao: false,
    google: false,
    apple: false,
  });
  const [hasEmailCredential, setHasEmailCredential] = useState(false);
  const [emailCredentialOpen, setEmailCredentialOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Phone verification — handled in PhoneVerifyModal
  const [verifiedPhoneMasked, setVerifiedPhoneMasked] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  // ADMIN 롤일 때만 관리자 콘솔 진입점 노출
  const [isAdmin, setIsAdmin] = useState(false);

  const filteredSuggestions = HEADER_SEARCH_KEYWORD_SUGGESTIONS.filter((item) =>
    item.toLowerCase().includes(headerSearchQuery.toLowerCase()),
  );

  const onHeaderSearchSubmit = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) return;
      navigate(`/search?q=${encodeURIComponent(q)}`);
    },
    [navigate],
  );

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!headerSearchRef.current?.contains(event.target as Node)) {
        setHeaderSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getMyAccount()
      .then((account) => {
        if (cancelled) return;
        setEmail(account.email);
        if (account.phoneVerified) {
          setVerifiedPhoneMasked(account.phoneMasked ?? '');
          setPhoneVerified(true);
        }
      })
      .catch(() => {
        // Account details are non-blocking for the settings shell.
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    getSession()
      .then((session) => {
        if (cancelled) return;
        setIsAdmin(hasSessionRole(session, 'ADMIN'));
      })
      .catch(() => {
        // 세션 조회 실패 시 관리자 메뉴만 숨긴다.
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    getMarketingConsent()
      .then((view) => {
        if (cancelled) return;
        setMarketingOptIn(view.granted);
      })
      .catch(() => {
        if (cancelled) return;
        setMarketingError('마케팅 동의 상태를 불러오지 못했습니다.');
        setMarketingOptIn(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleMarketingToggle = () => {
    if (marketingOptIn === null || marketingPending) return;
    const prev = marketingOptIn;
    const next = !prev;
    setMarketingOptIn(next);
    if (marketingDebounceRef.current !== null) {
      clearTimeout(marketingDebounceRef.current);
    }
    marketingDebounceRef.current = setTimeout(async () => {
      setMarketingPending(true);
      try {
        const view = await updateMarketingConsent({ granted: next });
        setMarketingOptIn(view.granted);
        setMarketingError(null);
      } catch {
        setMarketingOptIn(prev);
        setMarketingError('변경 실패. 잠시 후 다시 시도해주세요.');
      } finally {
        setMarketingPending(false);
      }
    }, 300);
  };

  const loadLinked = useCallback(() => {
    getLinkedProviders()
      .then((result) => {
        const map: Record<AccountLinkProvider, boolean> = { kakao: false, google: false, apple: false };
        for (const p of result.providers) {
          const key = p.provider.toLowerCase() as AccountLinkProvider;
          if (key in map) map[key] = true;
        }
        setLinked(map);
        setHasEmailCredential(result.hasEmailCredential ?? false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLinked();
  }, [loadLinked]);

  const toggleLink = async (p: AccountLinkProvider) => {
    if (linked[p]) {
      const providerEnum = p.toUpperCase() as 'KAKAO' | 'GOOGLE' | 'APPLE';
      try {
        await socialUnlink(providerEnum);
        setLinked((prev) => ({ ...prev, [p]: false }));
      } catch {
        alert('계정 연동 해제에 실패했습니다.');
      }
    } else {
      const providerEnum = p.toUpperCase() as 'KAKAO' | 'GOOGLE' | 'APPLE';
      startOAuth(providerEnum, 'link');
    }
  };

  return (
    <main className="account-settings-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onGuestCta={() => navigate('/login')}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery('');
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() =>
          setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))
        }
        onSearchSubmit={onHeaderSearchSubmit}
        onSuggestionSelect={(value) => {
          setHeaderSearchOpen(false);
          onHeaderSearchSubmit(value);
        }}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <div className="account-settings-page__main">
        <div className="account-settings">
          <header className="account-settings__header">
            <button
              type="button"
              className="account-settings__back"
              onClick={() => navigate(-1)}
              aria-label="뒤로"
            >
              <span className="account-settings__back-chevron" aria-hidden>
                <ChevronIcon />
              </span>
            </button>
            <h1 className="account-settings__title">계정 설정</h1>
          </header>

          <div className="account-settings__stack">
            <section className="account-settings__section">
              <span className="account-settings__label" id="account-email-label">
                이메일
              </span>
              <div
                className="account-settings__readonly"
                aria-labelledby="account-email-label"
              >
                {email}
              </div>
            </section>

            <section className="account-settings__section">
              <span className="account-settings__label">비밀번호 변경</span>
              <button
                type="button"
                className="account-settings__password-btn"
                onClick={() => setChangePasswordOpen(true)}
              >
                비밀번호 변경
              </button>
            </section>

            <section className="account-settings__section">
              <span className="account-settings__label">휴대폰 번호</span>
              {phoneVerified ? (
                <div className="account-settings__readonly">
                  휴대폰 인증 완료{verifiedPhoneMasked ? ` (${verifiedPhoneMasked})` : ''}
                </div>
              ) : (
                <button
                  className="account-settings__password-btn"
                  onClick={() => setPhoneModalOpen(true)}
                  type="button"
                >
                  휴대폰 인증하기
                </button>
              )}
            </section>

            <section className="account-settings__section">
              <span className="account-settings__label">차단 관리</span>
              <button
                type="button"
                className="account-settings__password-btn"
                onClick={() => navigate('/account/blocked-users')}
              >
                차단한 사용자 관리
              </button>
            </section>

            {isAdmin ? (
              <section className="account-settings__section">
                <span className="account-settings__label">관리자</span>
                <button
                  type="button"
                  className="account-settings__password-btn"
                  onClick={() => navigate('/admin')}
                >
                  관리자 콘솔
                </button>
              </section>
            ) : null}

            <section className="account-settings__section">
              <span className="account-settings__label">선택 약관 동의</span>
              <div className="account-settings__marketing-row">
                <div className="account-settings__marketing-left">
                  <button
                    type="button"
                    className="account-settings__consent-toggle"
                    aria-pressed={marketingOptIn ?? false}
                    disabled={marketingOptIn === null || marketingPending}
                    onClick={handleMarketingToggle}
                    aria-label="마케팅 정보 수신 동의"
                  >
                    {marketingOptIn ? (
                      <svg
                        aria-hidden
                        fill="none"
                        height="10"
                        viewBox="0 0 12 10"
                        width="12"
                      >
                        <path
                          d="M1 5l3.5 3.5L11 1"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                        />
                      </svg>
                    ) : (
                      <svg
                        aria-hidden
                        fill="none"
                        height="7"
                        viewBox="0 0 10 7"
                        width="10"
                      >
                        <path
                          d="M1 1h8M1 3.5h5"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeWidth="1.2"
                        />
                      </svg>
                    )}
                  </button>
                  <div className="account-settings__consent-text">
                    <span className="account-settings__consent-optional">
                      (선택)
                    </span>
                    <span className="account-settings__consent-main">
                      마케팅 정보 수신동의
                    </span>
                  </div>
                </div>
                <a
                  className="account-settings__marketing-chevron"
                  href="/signup/terms"
                  aria-label="약관 자세히 보기"
                >
                  <ChevronIcon />
                </a>
              </div>
              {marketingError ? (
                <p style={{ marginTop: '6px', color: '#e74c3c', fontSize: '13px' }}>
                  {marketingError}
                </p>
              ) : null}
            </section>

            {/* 소셜 로그인 비노출 시 계정연동 섹션(라벨 포함) 통째 숨김 (SOCIAL_LOGIN_ENABLED) */}
            {SOCIAL_LOGIN_ENABLED ? (
            <section className="account-settings__section">
              <span className="account-settings__label">계정연동</span>
              <div className="account-settings__link-box">
                <div className="account-settings__link-row">
                  <div className="account-settings__link-left">
                    <span
                      className="account-settings__sns-icon account-settings__sns-icon--email"
                      aria-hidden
                    >
                      <EmailGlyph />
                    </span>
                    <span className="account-settings__sns-name">이메일 로그인</span>
                  </div>
                  <div className="account-settings__link-status">
                    {hasEmailCredential ? (
                      <>
                        <LinkDoneCheck />
                        <span className="account-settings__link-done">
                          설정됨
                        </span>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="account-settings__link-action"
                        onClick={() => setEmailCredentialOpen(true)}
                      >
                        등록하기
                      </button>
                    )}
                  </div>
                </div>
                {SNS_LINK_ROWS.filter(({ key }) =>
                  WEB_OAUTH_PROVIDERS.includes(key.toUpperCase() as OAuthProvider)
                ).map(({ key, name, Glyph }) => (
                  <div className="account-settings__link-row" key={key}>
                    <div className="account-settings__link-left">
                      <span
                        className={`account-settings__sns-icon account-settings__sns-icon--${key}`}
                        aria-hidden
                      >
                        <Glyph />
                      </span>
                      <span className="account-settings__sns-name">{name}</span>
                    </div>
                    <div className="account-settings__link-status">
                      {linked[key] ? (
                        <>
                          <LinkDoneCheck />
                          <span className="account-settings__link-done">
                            연동완료
                          </span>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="account-settings__link-action"
                          onClick={() => toggleLink(key)}
                        >
                          연동하기
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
            ) : null}
          </div>

          <div className="account-settings__withdraw-wrap">
            <button
              type="button"
              className="account-settings__withdraw"
              onClick={async () => {
                if (!window.confirm('정말 탈퇴하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
                try {
                  await deactivateAccount();
                  clearAuthSession();
                  clearBlockedUserIds();
                  navigate('/');
                } catch {
                  alert('회원 탈퇴에 실패했습니다.');
                }
              }}
            >
              회원탈퇴
            </button>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <EmailCredentialModal
        onClose={() => setEmailCredentialOpen(false)}
        onSuccess={() => {
          setEmailCredentialOpen(false);
          setHasEmailCredential(true);
          loadLinked();
        }}
        open={emailCredentialOpen}
      />

      <PhoneVerifyModal
        onClose={() => setPhoneModalOpen(false)}
        onVerified={(masked) => {
          setVerifiedPhoneMasked(masked);
          setPhoneVerified(true);
          setPhoneModalOpen(false);
        }}
        open={phoneModalOpen}
      />

      <HomeFooter />
    </main>
  );
}
